import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { Table, GenericModal, SearchActions, Button, unwrapTrpcData } from '@org/ui';
import { trpc } from '@org/clients/school';
import { JourneyMultiStepForm } from './journey-multi-step-form';
import { useTranslation } from 'react-i18next';

type JourneysListProps = {
  onCreateJourney?: () => void;
  onEditJourney?: (journey: any) => void;
};

export const JourneysList: React.FC<JourneysListProps> = ({ onCreateJourney, onEditJourney }) => {
  const { t } = useTranslation('admin');
  const { data } = trpc.journeys.list.useQuery();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [filterValue, setFilterValue] = React.useState("");
  const [journeyData, setJourneyData] = React.useState<ReturnType<typeof unwrapTrpcData<typeof data>>>(null);
  const [selectedJourney, setSelectedJourney] = React.useState(null);
  const [isCreating, setIsCreating] = React.useState(false);

  React.useEffect(() => {
    setJourneyData(unwrapTrpcData(data) ?? null);
  }, [data]);

  const selectJourney = (row: any) => {
    if (onEditJourney) {
      onEditJourney(row.original);
      return;
    }

    setIsCreating(false);
    setModalOpen(true)
    setSelectedJourney(row.original);
  }

  const openNewJourneyForm = () => {
    if (onCreateJourney) {
      onCreateJourney();
      return;
    }

    setSelectedJourney(null);
    setIsCreating(true);
    setModalOpen(true);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  const onJourneyFormSubmit = () => {
    setModalOpen(false);
  }

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor((row: any) => row.name ? `${row.name}` : t('journeys.list.noName'), {
      id: 'name',
      header: t('journeys.list.table.columns.name'),
    }),
    columnHelper.accessor((row: any) => row.description || '', {
      id: 'description',
      header: t('journeys.list.table.columns.description'),
    }),
    columnHelper.display({
      id: 'edit',
      header: t('journeys.list.table.columns.actions'),
      cell: (info) => (
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => selectJourney(info.row)}
          >
            {t('journeys.list.actions.edit')}
          </Button>
        </div>
      ),
    }),
  ];

  return (
    <div>
      <div><SearchActions
          onClickAdd={openNewJourneyForm}
          onFilterChange={handleFilterChange}
        />
        </div>
      <div><Table
        tableData={journeyData as any}
        columns={columns}
        getRowId={(row: any) => row.journeyId}
        globalFilter={filterValue}
      /></div>
      {!onCreateJourney && !onEditJourney && modalOpen ? (
        <div><GenericModal
          show={modalOpen}
          onClose={() => setModalOpen(false)}
          title={isCreating ? t('journeys.list.modal.createTitle') : t('journeys.list.modal.editTitle')}
          body={
            <div>
              <JourneyMultiStepForm
                defaultValues={selectedJourney || {}}
                onSubmit={onJourneyFormSubmit}
                onClose={() => setModalOpen(false)}
                isEditing={!isCreating}
              />
            </div>
          }
          footer={
            <>
            </>
          }
          size="xl"
          centered
        />
        </div>
      ) : null}
    </div>
  )
}
